/**
 * Shrinks a picked image in the browser before upload: the longest side is
 * capped at `maxSide` (never enlarged) and it is re-encoded as WebP. A browser
 * that cannot encode WebP (older Safari) hands back PNG, which keeps
 * transparency; at this size either stays far under the storage cap.
 */
export async function downscaleImage(file: Blob, maxSide = 512, quality = 0.85): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("Couldn't read that image. Try a JPG or PNG.");
  }

  try {
    const { width, height } = scaledSize(bitmap.width, bitmap.height, maxSide);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("Couldn't read that image. Try a JPG or PNG.");
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', quality));
    if (!blob) throw new Error("Couldn't read that image. Try a JPG or PNG.");
    return blob;
  } finally {
    bitmap.close();
  }
}

/** Fits width x height inside maxSide x maxSide, keeping the aspect ratio. */
export function scaledSize(width: number, height: number, maxSide: number): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
